import { useEffect, useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import axios from "axios"
import { Link, useParams } from "react-router-dom"
import { api } from "@/api/client"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import type { Verbrauchswert, Zaehler } from "@/api/types"

function ZaehlerstandSheet({
  open,
  onOpenChange,
  zaehlerId,
  aktuellerStand,
  maxZaehlerstand,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  zaehlerId: number
  aktuellerStand: string | number | null | undefined
  maxZaehlerstand: string | number | null | undefined
}) {
  const queryClient = useQueryClient()
  const [neuerStand, setNeuerStand] = useState("")
  const [verbrauchsdatum, setVerbrauchsdatum] = useState(() => new Date().toISOString().slice(0, 10))
  const [fehler, setFehler] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setNeuerStand("")
    setVerbrauchsdatum(new Date().toISOString().slice(0, 10))
    setFehler(null)
  }, [open])

  const einbuchen = useMutation({
    mutationFn: async () =>
      (
        await api.post(`/zaehler/${zaehlerId}/verbrauch-einbuchen`, {
          neuerStand,
          verbrauchsdatum,
        })
      ).data,
    onSuccess: () => {
      setFehler(null)
      queryClient.invalidateQueries({ queryKey: ["zaehler", zaehlerId] })
      queryClient.invalidateQueries({ queryKey: ["verbrauchswerte", zaehlerId] })
      onOpenChange(false)
    },
    onError: (err: unknown) => {
      if (axios.isAxiosError(err)) {
        setFehler(err.response?.data?.detail ?? "Erfassung fehlgeschlagen")
      } else {
        setFehler("Erfassung fehlgeschlagen")
      }
    },
  })

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent sheetKey="energie.zaehlerstand" className="overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Zählerstand erfassen</SheetTitle>
          <SheetDescription>
            Aktueller Stand: {aktuellerStand ?? "—"}
            {maxZaehlerstand && ` · Rollover-Obergrenze: ${maxZaehlerstand}`}
          </SheetDescription>
        </SheetHeader>
        <form
          className="grid gap-4 py-4"
          onSubmit={(e) => {
            e.preventDefault()
            einbuchen.mutate()
          }}
        >
          <div className="space-y-1.5">
            <label className="block text-sm text-slate-600" htmlFor="verbrauchsdatum">
              Verbrauchsdatum
            </label>
            <input
              id="verbrauchsdatum"
              type="date"
              required
              value={verbrauchsdatum}
              onChange={(e) => setVerbrauchsdatum(e.target.value)}
              className="h-9 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-400/60"
            />
          </div>
          <div className="space-y-1.5">
            <label className="block text-sm text-slate-600" htmlFor="neuerStand">
              Neuer Zählerstand
            </label>
            <input
              id="neuerStand"
              type="number"
              step="0.001"
              required
              value={neuerStand}
              onChange={(e) => setNeuerStand(e.target.value)}
              className="h-9 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-400/60"
            />
          </div>
          {fehler && <p className="text-sm text-red-600">{fehler}</p>}
          <SheetFooter>
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="inline-flex h-9 items-center gap-2 rounded-md border border-slate-300 bg-white px-4 text-sm font-medium text-slate-700 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={einbuchen.isPending}
              className="inline-flex h-9 items-center gap-2 rounded-md bg-slate-900 px-4 text-sm font-medium text-white transition-colors hover:bg-slate-700 disabled:pointer-events-none disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
            >
              {einbuchen.isPending ? "speichert…" : "Einbuchen"}
            </button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  )
}

/**
 * Inhalt der Zähler-Detailansicht (Karten-Fähigkeits-Audit §4: 🟢, Dual-Muster
 * wie rmnc-technik `TicketDetailContent`). Wird sowohl im Sheet (Zeilen-Klick
 * aus `ZaehlerListPage`) als auch auf der Route `/zaehler/:id` (Deep-Link-
 * Fallback) verwendet.
 *
 * `onNavigateZaehler` erlaubt der Sheet-Variante, beim Klick auf einen
 * Unterzähler den Sheet-Inhalt zu wechseln statt zu navigieren; ohne die Prop
 * (Route-Variante) navigiert ein normaler `<Link>`.
 */
export function ZaehlerDetailContent({
  zaehlerId,
  onNavigateZaehler,
}: {
  zaehlerId: number
  onNavigateZaehler?: (id: number) => void
}) {
  const [sheetOffen, setSheetOffen] = useState(false)

  const zaehlerQuery = useQuery({
    queryKey: ["zaehler", zaehlerId],
    queryFn: async () => (await api.get<Zaehler>(`/zaehler/${zaehlerId}`)).data,
  })

  const unterzaehlerQuery = useQuery({
    queryKey: ["zaehler", zaehlerId, "unterzaehler"],
    queryFn: async () => (await api.get<Zaehler[]>(`/zaehler/${zaehlerId}/unterzaehler`)).data,
  })

  const verlaufQuery = useQuery({
    queryKey: ["verbrauchswerte", zaehlerId],
    queryFn: async () =>
      (await api.get<Verbrauchswert[]>("/verbrauchswerte", { params: { zaehlerId } })).data,
  })

  if (zaehlerQuery.isLoading) return <p className="text-slate-500">lädt…</p>
  if (zaehlerQuery.isError || !zaehlerQuery.data) {
    return <p className="text-red-600">Zähler nicht gefunden</p>
  }
  const z = zaehlerQuery.data

  return (
    <div>
      {!onNavigateZaehler && (
        <Link to="/zaehler" className="text-sm text-slate-500 hover:underline">
          ← Zurück zur Liste
        </Link>
      )}
      <h1 className="mt-2 text-2xl font-semibold text-slate-900">{z.displayName}</h1>
      <p className="mt-1 text-sm text-slate-500">
        {z.anlageBeschreibung ?? "Keine Anlagen-Zuordnung"} · Kostenstelle:{" "}
        {z.kostenstelleNavision ?? "—"}
      </p>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <section className="rounded-lg border border-slate-200 bg-white p-4">
          <h2 className="font-medium text-slate-900">Zählerstand</h2>
          <p className="mt-1 text-xs text-slate-500">
            Aktueller Stand: {z.letzterStand ?? "—"}
            {z.maxZaehlerstand && ` · Rollover-Obergrenze: ${z.maxZaehlerstand}`}
          </p>
          <button
            type="button"
            onClick={() => setSheetOffen(true)}
            className="mt-4 inline-flex h-9 items-center gap-2 rounded-md bg-slate-900 px-4 text-sm font-medium text-white transition-colors hover:bg-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
          >
            Zählerstand erfassen
          </button>
          <ZaehlerstandSheet
            open={sheetOffen}
            onOpenChange={setSheetOffen}
            zaehlerId={zaehlerId}
            aktuellerStand={z.letzterStand}
            maxZaehlerstand={z.maxZaehlerstand}
          />
        </section>

        <section className="rounded-lg border border-slate-200 bg-white p-4">
          <h2 className="font-medium text-slate-900">Unterzähler</h2>
          <ul className="mt-3 space-y-1 text-sm">
            {unterzaehlerQuery.data?.map((u) =>
              onNavigateZaehler ? (
                <li key={u.id}>
                  <button
                    type="button"
                    onClick={() => onNavigateZaehler(u.id)}
                    className="text-slate-700 hover:underline"
                  >
                    {u.displayName}
                  </button>
                </li>
              ) : (
                <li key={u.id}>
                  <Link to={`/zaehler/${u.id}`} className="text-slate-700 hover:underline">
                    {u.displayName}
                  </Link>
                </li>
              ),
            )}
            {unterzaehlerQuery.data?.length === 0 && (
              <li className="text-slate-400">Keine Unterzähler</li>
            )}
          </ul>
        </section>
      </div>

      <section className="mt-6 overflow-x-auto rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
        <h2 className="font-medium text-slate-900">Verlauf (letzte Buchungen)</h2>
        <table className="mt-3 w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-wide text-slate-400">
            <tr>
              <th className="py-2.5 font-semibold">Tag</th>
              <th className="py-2.5 font-semibold">Verbrauch</th>
              <th className="py-2.5 font-semibold">Quelle</th>
              <th className="py-2.5 font-semibold">Rollover</th>
            </tr>
          </thead>
          <tbody>
            {[...(verlaufQuery.data ?? [])]
              .reverse()
              .slice(0, 16)
              .map((w) => (
                <tr key={w.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="py-2.5 text-slate-700">{w.tagesdatum}</td>
                  <td className="py-2.5 text-slate-700">{w.verbrauch}</td>
                  <td className="py-2.5 text-slate-700">{w.quelle}</td>
                  <td className="py-2.5 text-slate-700">{w.rollover ? "ja" : ""}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </section>
    </div>
  )
}

/** Route-Fallback `/zaehler/:id` (Deep-Link) — Sheet-Aufruf: siehe `ZaehlerListPage`. */
export function ZaehlerDetailPage() {
  const { id } = useParams<{ id: string }>()
  const zaehlerId = Number(id)
  return <ZaehlerDetailContent zaehlerId={zaehlerId} />
}
